import useSession from '@/suppliers/hooks/useSession'
import { CheckIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import React from 'react'
import { Filter, Pagination, Product, UseSelection } from '../../types'
import fields from '../data/productFields'
import useConfig from '../hooks/useConfig'
import useFilters from '../hooks/useFilters'
import { Product as Product2 } from '../hooks/useProducts'
import Money from '../modules/Money'
import ProductM from '../modules/Product'
import Time from '../modules/Time'
import UserContext from '../modules/User'
import Filters from './Filters'
import Layout from './Layout'
import Link from './Link'
import MoneyCell from './MoneyCell'
import PaginationC from './Pagination'
import Spinner from './Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from './Table'

const ProductsTable = ({
  page,
  selection,
  filters,
  updatePage,
  updateFilters,
  productsSearch,
  children,
}: {
  page: number
  selection: UseSelection
  filters: Filter[]
  updatePage: (newPage: number) => void
  updateFilters: (newFilters: Filter[]) => void
  productsSearch?: { products: (Product | Product2)[]; pagination: Pagination }
  children: React.ReactNode
}) => {
  const config = useConfig()
  const session = useSession()
  const isAdmin = UserContext.isAdmin(session.user)
  const [stagedFilters, filtersApi] = useFilters(filters)
  const shippingRate = (product) => product.shippingRate

  return (
    <Layout>
      <Layout.Section type="table">
        <Filters
          filters={filters}
          stagedFilters={stagedFilters}
          fields={fields}
          filtersApi={filtersApi}
          onApply={() => {
            selection.deselectAll()
            updateFilters(stagedFilters)
          }}
        />
        {productsSearch && selection.selectedIds.length > 0 && (
          <div
            className={classNames('px-4 pb-2 flex w-full bg-gray-50', {
              hidden: selection.selectedIds.length === 0,
            })}
          >
            <span className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white">
              <input
                name="select-all2"
                type="checkbox"
                checked={productsSearch.products.length === selection.selectedIds.length}
                onClick={(event) => {
                  event.stopPropagation()
                }}
                onChange={() => selection.togglePage()}
              />
              <label
                htmlFor="select-all2"
                className="whitespace-nowrap pl-4 pr-2 text-gray-700 tracking-normal normal-case text-base sm:mt-0 sm:w-auto sm:text-sm"
              >
                {selection.all && (
                  <>{productsSearch.pagination.totalResults.toLocaleString()} Selected</>
                )}
                {!selection.all && <>{selection.selectedIds.length.toLocaleString()} Selected</>}
              </label>
            </span>
            <div className="flex w-full">{children}</div>
          </div>
        )}
        <Table>
          <Thead>
            <tr>
              <Th>
                {productsSearch && selection.selectedIds.length === 0 && (
                  <input
                    name="select-all"
                    type="checkbox"
                    checked={productsSearch.products.length === selection.selectedIds.length}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                    onChange={() => selection.togglePage()}
                  />
                )}
              </Th>
              <Th>Image</Th>
              <Th>Name</Th>
              {isAdmin && <Th>Store</Th>}
              {session.featureFlags.multiSource && <Th>Source</Th>}
              <Th>SKU</Th>
              <Th>MPN</Th>
              <Th>Brand</Th>
              <Th>Category</Th>
              <Th>Sale Price</Th>
              <Th>List Price</Th>
              {(isAdmin || !session.store.shipsFree) && <Th>Shipping Cost</Th>}
              <Th>Quantity</Th>
              <Th>Availability</Th>
              <Th>Enabled</Th>
              {config.app === 'admin' && <Th className="whitespace-normal">Merchant Enabled</Th>}
              {config.app === 'admin' && <Th className="whitespace-normal">Merchant Group</Th>}
              <Th>Updated</Th>
              <Th>Added</Th>
            </tr>
          </Thead>
          <Tbody>
            {productsSearch?.products.map((product) => (
              <Tr key={product.id}>
                <Td>
                  <input
                    checked={selection.isSelected(product.id)}
                    type="checkbox"
                    onChange={() => selection.toggle(product.id)}
                  />
                </Td>
                <Td>
                  <img src={ProductM.primaryImageURL(product, 'card')} alt=" " className="h-10" />
                </Td>
                <Td>
                  <Link.T to={`/products/${product.id}`}>{product.name}</Link.T>
                </Td>
                {isAdmin && <Td>{product.store.name}</Td>}
                {session.featureFlags.multiSource && <Td>{product.sourceProduct.source.name}</Td>}
                <Td>{product.sku}</Td>
                <Td>{product.mpn}</Td>
                <Td>{product.brand?.name}</Td>
                <Td>{product.category?.name}</Td>
                <MoneyCell amount={Money.fromDecimal(product.salePrice, 'USD')} />
                <MoneyCell amount={Money.fromDecimal(product.listPrice, 'USD')} />
                {(isAdmin || !session.store.shipsFree) && (
                  <MoneyCell amount={Money.fromDecimal(shippingRate(product), 'USD')} />
                )}
                <Td>{product.quantity?.toLocaleString()}</Td>
                <Td>{product.availability}</Td>
                <Td>{product.enabled && <CheckIcon className="w-5 h-5" />}</Td>
                {config.app === 'admin' && (
                  <Td>
                    {'merchantEnabled' in product && product.merchantEnabled && (
                      <CheckIcon className="w-5 h-5" />
                    )}
                  </Td>
                )}
                {config.app === 'admin' && (
                  <Td>{'merchantGroup' in product && product.merchantGroup}</Td>
                )}
                <Td>{Time.toString(product.updatedAt)}</Td>
                <Td>{Time.toString(product.addedAt)}</Td>
              </Tr>
            ))}

            {!productsSearch && (
              <Tr>
                <Td colSpan={99}>
                  <Spinner />
                </Td>
              </Tr>
            )}

            {productsSearch?.products.length === 0 && (
              <Tr>
                <Td colSpan={99}>No products found.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Layout.Section>

      <Layout.Section type="full">
        <PaginationC pagination={productsSearch?.pagination} page={page} updatePage={updatePage} />
      </Layout.Section>
    </Layout>
  )
}

export default ProductsTable
